import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useEvents } from '../../context/EventsContext';
import { formatDate, formatPrice, reservationStatuses, mediaUrl } from '../../utils/format';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Users, CheckCircle2, XCircle, Timer, Clock,
  UserCheck, UserX, Mail, Phone, ChevronDown, ChevronRight,
  BarChart3
} from 'lucide-react';

const formatDateTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
};

const statusIcon = {
  confirmed: <CheckCircle2 className="w-3.5 h-3.5" />,
  paid: <Timer className="w-3.5 h-3.5" />,
  rejected: <XCircle className="w-3.5 h-3.5" />,
  expired: <XCircle className="w-3.5 h-3.5" />,
  cancelled: <XCircle className="w-3.5 h-3.5" />,
  pending: <Clock className="w-3.5 h-3.5" />
};

const AdminEventReservations = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { getEventDetail, getReservations } = useEvents();

  const [event, setEvent] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        const [ev, res] = await Promise.all([
          getEventDetail(eventId),
          getReservations(eventId)
        ]);
        setEvent(ev);
        setReservations(res || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [eventId, getEventDetail, getReservations]);

  const stats = useMemo(() => {
    const total = reservations.length;
    const confirmed = reservations.filter(r => r.status === 'confirmed').length;
    const pending = reservations.filter(r => r.status === 'paid').length;
    const rejected = reservations.filter(r => r.status === 'rejected').length;
    const expired = reservations.filter(r => r.status === 'expired').length;
    const cancelled = reservations.filter(r => r.status === 'cancelled').length;
    const attended = reservations.filter(r => r.attended).length;
    const noShow = reservations.filter(r => r.status === 'confirmed' && !r.attended).length;
    const totalChairs = reservations.reduce((s, r) => s + (r.chairIds?.length || 0), 0);
    const revenue = reservations
      .filter(r => r.status === 'confirmed')
      .reduce((s, r) => s + (parseFloat(r.totalPrice) || 0), 0);
    return { total, confirmed, pending, rejected, expired, cancelled, attended, noShow, totalChairs, revenue };
  }, [reservations]);

  const filtered = filterStatus === 'all'
    ? reservations
    : filterStatus === 'attended'
      ? reservations.filter(r => r.attended)
      : filterStatus === 'no_show'
        ? reservations.filter(r => r.status === 'confirmed' && !r.attended)
        : reservations.filter(r => r.status === filterStatus);

  if (loading) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Cargando...</div>;
  if (!event) return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-muted-foreground">Evento no encontrado</div>;

  const filters = [
    { key: 'all', label: 'Todas', count: stats.total },
    { key: 'confirmed', label: 'Confirmadas', count: stats.confirmed },
    { key: 'paid', label: 'Pendientes', count: stats.pending },
    { key: 'attended', label: 'Asistieron', count: stats.attended },
    { key: 'no_show', label: 'No asistieron', count: stats.noShow },
    { key: 'expired', label: 'Expiradas', count: stats.expired },
    { key: 'rejected', label: 'Rechazadas', count: stats.rejected },
    { key: 'cancelled', label: 'Canceladas', count: stats.cancelled },
  ];

  return (
    <div data-testid="event-reservations-page" className="min-h-screen bg-[#050505]">
      <header className="border-b border-border bg-secondary/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/events/${eventId}`)} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Volver</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-serif font-semibold text-foreground truncate">Reservaciones</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate">{event.name} · {formatDate(event.date)}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <div className="bg-secondary border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-xs text-muted-foreground">Total</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.total}</p>
            <p className="text-xs text-muted-foreground">{stats.totalChairs} sillas</p>
          </div>
          <div className="bg-secondary border border-emerald-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-muted-foreground">Confirmadas</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.confirmed}</p>
            <p className="text-xs text-primary">{formatPrice(stats.revenue, event.currency)}</p>
          </div>
          <div className="bg-secondary border border-amber-500/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <Timer className="w-4 h-4 text-amber-400" />
              <span className="text-xs text-muted-foreground">Pendientes</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.pending}</p>
          </div>
          <div className="bg-secondary border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserCheck className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-muted-foreground">Asistieron</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.attended}</p>
          </div>
          <div className="bg-secondary border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserX className="w-4 h-4 text-red-400" />
              <span className="text-xs text-muted-foreground">No asistieron</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{stats.noShow}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {filters.map(f => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all
                ${filterStatus === f.key
                  ? 'bg-primary text-white'
                  : 'bg-secondary border border-border text-muted-foreground hover:border-primary/40'
                }`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>

        {/* Reservations List */}
        <div className="bg-secondary border border-border rounded-lg overflow-hidden">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No hay reservaciones con este filtro</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {filtered.map(res => {
                const statusConf = reservationStatuses[res.status] || reservationStatuses.pending;
                const isExpanded = expandedId === res.id;
                const chairs = res.chairs || [];
                const total = parseFloat(res.totalPrice) || 0;

                return (
                  <div key={res.id}>
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : res.id)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/20 transition-colors"
                    >
                      <div className="shrink-0">
                        {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">{res.full_name}</span>
                          {res.attended && (
                            <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded font-medium">Asistió</span>
                          )}
                          {res.status === 'confirmed' && !res.attended && (
                            <span className="text-[10px] bg-red-500/15 text-red-400/70 px-1.5 py-0.5 rounded font-medium">No asistió</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground/70 mt-0.5">
                          {(res.chairIds || []).length} silla{(res.chairIds || []).length !== 1 ? 's' : ''} · {formatPrice(total, event.currency)} · {formatDateTime(res.created_at)}
                        </p>
                      </div>
                      <Badge className={`text-[10px] shrink-0 ${statusConf.color}`}>
                        {statusIcon[res.status]}
                        <span className="ml-1">{statusConf.label}</span>
                      </Badge>
                    </button>

                    {isExpanded && (
                      <div className="px-4 pb-4 pl-11 space-y-3">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="flex items-center gap-1.5 text-muted-foreground">
                            <Mail className="w-3 h-3 shrink-0" />
                            <span className="truncate">{res.email}</span>
                          </div>
                          <a
                            href={`https://wa.me/${(res.whatsapp || '').replace(/[\s()\-+]/g, '')}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 text-muted-foreground hover:text-emerald-400 transition-colors"
                          >
                            <Phone className="w-3 h-3 shrink-0" />
                            <span className="underline underline-offset-2">{res.whatsapp}</span>
                          </a>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          <span>Método: {res.paymentMethod || '-'}</span>
                          <span className="mx-2">·</span>
                          <span>UUID: <span className="font-mono text-muted-foreground/60">{res.uuid}</span></span>
                        </div>
                        {chairs.length > 0 && (
                          <div className="p-2 bg-muted/20 rounded text-xs space-y-1">
                            {chairs.map(c => (
                              <div key={c.id} className="flex justify-between text-muted-foreground">
                                <span>Silla {c.label}</span>
                                <span>{formatPrice(c.price, event.currency)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {res.note && (
                          <p className="text-xs text-muted-foreground italic">Nota: {res.note}</p>
                        )}
                        {res.paymentProofUrl && (
                          <div data-testid={`payment-proof-${res.id}`}>
                            <p className="text-[10px] uppercase tracking-wide text-muted-foreground/60 mb-1.5">Comprobante de pago</p>
                            <a
                              href={mediaUrl(res.paymentProofUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block relative group/proof rounded-md overflow-hidden border border-border bg-background"
                            >
                              <img
                                src={mediaUrl(res.paymentProofUrl)}
                                alt="Comprobante"
                                className="w-full max-h-56 object-contain bg-black/40"
                              />
                              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/proof:opacity-100 transition-opacity flex items-center justify-center text-xs text-white">
                                Click para ver completo
                              </div>
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminEventReservations;
